import React, { useContext, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowRightLeft, ArrowUpRight, Building2, CircleUserRound, Clock3, Coins, FolderOpen, Globe2, KeyRound, Mail, MapPin,
  Phone, Table2, UsersRound, Workflow,
} from 'lucide-react';

import { AuthContext } from 'src/context/AuthContext';
import StagePage from 'src/stage/StagePage';
import { accessFor } from 'src/stage/crmNav';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import axiosInstance from 'src/common/axiosInstance';
import ApiKeysPage from 'src/common/apiKeys/ApiKeysPage';
import { CustomerSharingSettings } from 'src/common/customerSharing/CustomerSharing';
import { useWorkspace } from 'src/features/MiniCRM/Workspace/context/WorkspaceContext';
import { getWorkspaceOverview } from 'src/features/MiniCRM/Workspace/services/workspaceService';
import WorkspaceOrganisationConnection from 'src/features/MiniCRM/Workspace/components/Workspace/WorkspaceOrganisationConnection';
import 'src/stage/settings.css';
import './settings-crm.css';

// AUTO-CRM-32: grouped settings, laid out like Auto Appy's Settings (AUTO-319).
// Only Company profile, Customer sharing and API keys live here; the other
// entries link to the pages that already manage them.
function sectionsFor(access, keyCount) {
  return [
    { label: 'Workspace', items: [
      { id: 'workspace', label: 'Company profile', icon: Building2 },
      { id: 'sharing', label: 'Customer sharing', icon: ArrowRightLeft },
    ] },
    { label: 'CRM', items: [
      access.canManagePipelines && { id: 'pipelines', label: 'Pipelines', icon: Workflow, to: '/admin/pipelines' },
      access.canManageLeadFields && { id: 'leadtable', label: 'Lead table', icon: Table2, to: '/admin/lead-fields' },
      { id: 'documents', label: 'Documents', icon: FolderOpen, to: '/documents' },
      access.canViewTeams && { id: 'team', label: 'Team', icon: UsersRound, to: '/teams' },
    ].filter(Boolean) },
    { label: 'Developers', items: [
      { id: 'api-keys', label: 'API keys', icon: KeyRound, count: keyCount },
    ] },
    { label: 'You', items: [
      { id: 'profile', label: 'Profile', icon: CircleUserRound, href: autoAppyUrl('/profile') },
    ] },
  ];
}

const TITLES = {
  workspace: ['Workspace', 'Company profile', 'How your business appears across Auto Appy and in the CRM.'],
  sharing: ['Workspace', 'Customer sharing', 'Choose how new CRM records connect to your Auto Appy customer profiles.'],
  'api-keys': ['Developers', 'API keys', 'Connect websites, sheets and automations to the CRM with a key that works without signing in.'],
};

function SettingsMenu({ sections, active }) {
  return (
    <nav className="ws-card ws-set-nav" aria-label="Settings">
      {sections.map((section) => (
        <React.Fragment key={section.label}>
          <div className="ws-set-nav-label">{section.label}</div>
          {section.items.map((item) => {
            const Icon = item.icon;
            const content = <><Icon size={16} aria-hidden="true" /><span className="ws-grow">{item.label}</span>{item.count !== undefined && item.count !== null && <span className="ws-set-nav-count ws-num">{item.count}</span>}{(item.to || item.href) && <ArrowUpRight className="ws-set-nav-out" aria-hidden="true" />}</>;
            if (item.href) return <a key={item.id} className="ws-set-nav-item" href={item.href}>{content}</a>;
            return <Link key={item.id} className="ws-set-nav-item" to={item.to || `/settings/${item.id}`} aria-current={active === item.id ? 'page' : undefined}>{content}</Link>;
          })}
        </React.Fragment>
      ))}
    </nav>
  );
}

function CompanyProfile({ workspace, canEdit, onEdit }) {
  // The backend decides which fields count towards completion (same number as Home).
  const [serverPercent, setServerPercent] = useState(null);
  useEffect(() => {
    let active = true;
    if (workspace?.id) getWorkspaceOverview(workspace.id).then((data) => { if (active) setServerPercent(data?.setup?.profile_completion_percent ?? null); }).catch(() => {});
    return () => { active = false; };
  }, [workspace?.id]);
  if (!workspace) return <section className="ws-card ws-card-pad"><div className="ws-empty">Select a workspace to see its profile.</div></section>;
  const profileFields = ['name', 'legal_name', 'email', 'phone', 'website', 'industry', 'company_size', 'country', 'timezone', 'currency'];
  const filled = profileFields.filter((key) => workspace[key]).length;
  const percent = serverPercent ?? Math.round((filled / profileFields.length) * 100);
  const details = [
    ['Industry', workspace.industry], ['Company size', workspace.company_size], ['Country', workspace.country],
    ['Timezone', workspace.timezone], ['Currency', workspace.currency], ['Member since', workspace.created_at && !Number.isNaN(Date.parse(workspace.created_at)) ? new Date(workspace.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null],
  ];
  const contacts = [[Mail, workspace.email, workspace.email && `mailto:${workspace.email}`], [Phone, workspace.phone, workspace.phone && `tel:${workspace.phone}`], [Globe2, workspace.website, workspace.website], [MapPin, workspace.country, null]].filter(([, label]) => label);
  return (
    <>
      <section className="ws-card crm-set-profile ws-rise">
        <span className="crm-set-mark" aria-hidden="true">{(workspace.name || 'W').slice(0, 1).toUpperCase()}</span>
        <div className="ws-grow">
          <h2 className="ws-h2">{workspace.name || 'Workspace'}</h2>
          <p className="ws-muted">{workspace.legal_name || 'Add your registered company name'}</p>
          <div className="crm-set-contacts">
            {contacts.map(([Icon, label, href]) => (href
              ? <a key={label} href={href} target={Icon === Globe2 ? '_blank' : undefined} rel={Icon === Globe2 ? 'noreferrer' : undefined}><Icon size={14} />{label}</a>
              : <span key={label}><Icon size={14} />{label}</span>))}
          </div>
        </div>
        {canEdit && <button type="button" className="ws-btn ws-btn--quiet" onClick={onEdit}>Edit in Auto Appy<ArrowUpRight size={14} /></button>}
      </section>
      <section className="ws-card ws-card-pad ws-rise">
        <div className="crm-set-complete">
          <div className="ws-grow"><h2 className="ws-h3">Profile {percent}% complete</h2><p className="ws-muted">A complete profile fills in invoices, emails and messages that mention your business.</p></div>
          <div className="ws-meter" style={{ width: 180 }} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label="Profile completion"><span style={{ width: `${percent}%` }} /></div>
        </div>
        <dl className="crm-set-details">
          {details.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || <span className="ws-muted">Not set</span>}</dd></div>)}
        </dl>
        <p className="ws-muted crm-set-foot"><Clock3 size={13} />The profile is shared with every Auto Appy product. Changes are made in your organisation settings.</p>
      </section>
    </>
  );
}

export default function SettingsPage() {
  const { section = 'workspace' } = useParams();
  const { currentUser } = useContext(AuthContext);
  const { currentWorkspace, openEditWorkspace, fetchWorkspaces } = useWorkspace();
  const access = accessFor(currentUser);
  const [keyCount, setKeyCount] = useState(null);

  useEffect(() => {
    let active = true;
    axiosInstance.get('/api-keys', { params: { application: 'crm' } })
      .then((response) => {
        const keys = response.data?.data?.keys || [];
        if (active) setKeyCount(keys.filter((key) => !key.revoked_at && (!key.expires_at || new Date(key.expires_at) > new Date())).length);
      })
      .catch(() => { if (active) setKeyCount(null); });
    return () => { active = false; };
  }, [section]);

  if (!TITLES[section]) return <Navigate to="/settings/workspace" replace />;
  const [group, title, subtitle] = TITLES[section];
  const sections = sectionsFor(access, keyCount);
  const canEdit = currentWorkspace?.current_user_role === 'admin' || access.isAdmin;

  return (
    <StagePage className="ws-set crm-set" eyebrow={<>Settings<span className="crm-eyebrow-dot" />{group}</>} title={title} subtitle={subtitle}>
      <div className="ws-set-layout">
        <SettingsMenu sections={sections} active={section} />
        <div className="ws-set-main">
          {section === 'workspace' && <CompanyProfile workspace={currentWorkspace} canEdit={canEdit} onEdit={() => openEditWorkspace(currentWorkspace)} />}
          {section === 'sharing' && (
            <section className="crm-set-embed ws-rise">
              {currentWorkspace?.autoappy_workspace_id
                ? <CustomerSharingSettings key={currentWorkspace.id} appName="Sales" />
                : currentWorkspace
                  ? <WorkspaceOrganisationConnection key={currentWorkspace.id} workspace={currentWorkspace} onConnected={fetchWorkspaces} />
                  : <div className="ws-empty">Select a workspace first.</div>}
            </section>
          )}
          {section === 'api-keys' && (
            <div className="crm-set-keys">
              <ApiKeysPage application="crm" appName="CRM" endpoint="/api-keys" />
            </div>
          )}
          <p className="ws-muted crm-set-note"><Coins size={13} />Plans, billing and connected apps are managed in <a href={autoAppyUrl('/settings/connections')}>Auto Appy settings</a>.</p>
        </div>
      </div>
    </StagePage>
  );
}
